import { Button, Dropdown, Input } from '@/shared';
import { useEffect, useRef, useState } from 'react';
import { type HouseFiltersType } from '../types';
import { useManagersQuery } from '../hooks/useManagerQueries';

interface Option {
  label: string;
  value: string | number;
}

interface HouseFiltersProps {
  filters: HouseFiltersType;
  setFilters: React.Dispatch<React.SetStateAction<HouseFiltersType>>;
  totalHouses: number | undefined;
}

// ================== COMPONENT ==================
export const HouseFiltersComponent: React.FC<HouseFiltersProps> = ({
  filters,
  setFilters,
  totalHouses = 0,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  // === OPTIONS ===
  const timeOptions: Option[] = [
    { label: 'Last 7 Days', value: '7d' },
    { label: 'Last 30 Days', value: '30d' },
    { label: 'Last 3 Months', value: '90d' },
  ];

  const typeOptions: Option[] = [
    { label: 'Apartment', value: 'Apartment' },
    { label: 'Villa', value: 'Villa' },
  ];

  const bedRateOptions: Option[] = [
    { label: '0–25%', value: '0-25' },
    { label: '25–50%', value: '25-50' },
    { label: '50–75%', value: '50-75' },
    { label: '75–100%', value: '75-100' },
  ];

  // Fetch managers dynamically from API
  const { data: managersData, isLoading: isLoadingManagers } = useManagersQuery(
    {
      page: 1,
      limit: 100, // Get all managers for the dropdown
      status: 'ACTIVE',
      search: '',
      assignedHouses: '',
      location: '',
    },
  );

  // Transform managers data to dropdown options
  const managerOptions: Option[] = isLoadingManagers
    ? [{ label: 'Loading managers...', value: '' }]
    : managersData?.managers && managersData.managers.length > 0
    ? managersData.managers.map((manager) => ({
        label: manager.name,
        value: manager.id,
      }))
    : [{ label: 'No managers available', value: '' }];

  // === HANDLERS ===
  const handleSelect = (
    field: keyof HouseFiltersType,
    value: string | number,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };

  const handleStatusClick = (status: 'ACTIVE' | 'INACTIVE') => {
    setFilters((prev) => ({
      ...prev,
      status,
      page: 1,
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setFilters((prev) => ({
      ...prev,
      search: searchValue,
      page: 1,
    }));
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const locationValue = e.target.value;
    setFilters((prev) => ({
      ...prev,
      location: locationValue,
      page: 1,
    }));
  };

  const clearFilterHandler = () => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      limit: 10,
      search: '',
      status: 'ACTIVE',
      type: '',
      bedUtilization: '',
      programFeesOrder: undefined,
      location: '',
      assignedManager: '',
      timeRange: '',
    }));
  };

  // === OUTSIDE CLICK HANDLER ===
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setShowFilters(false);
      }
    };

    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters]);

  useEffect(() => console.log('-->filters ', filters), [filters]);

  // === RENDER ===
  return (
    <div className="w-full">
      <div className="flex flex-col lg:flex-row items-center justify-between">
        {/* Title */}
        <div className="mb-4">
          <p className="text-lg md:text-2xl text-text-primary font-bold">
            All Houses{' '}
            <span className="font-normal text-base md:text-xl">({totalHouses})</span>
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
          {/* Time Range */}
          <Dropdown
            options={timeOptions}
            value={filters.timeRange}
            placeholder="Select Range"
            onChange={(val) => handleSelect('timeRange', val as string)}
            className="!w-full md:!w-auto"
            buttonClassName="!min-w-fit !gap-4 text-text-secondary !w-full"
            optionsClassNames="!text-[16px]"
            labelClassName="!text-[16px]"
          />

          {/* Search */}
          <div className="flex items-center w-full md:w-[256px]">
            <Input
              className="rounded-lg !w-full !text-[16px]"
              placeholder="Search houses"
              value={filters.search}
              onChange={handleSearchChange}
              rightIcon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.6 18L10.3 11.7C9.8 12.1 9.225 12.4167 8.575 12.65C7.925 12.8833 7.23333 13 6.5 13C4.68333 13 3.14583 12.3708 1.8875 11.1125C0.629167 9.85417 0 8.31667 0 6.5C0 4.68333 0.629167 3.14583 1.8875 1.8875C3.14583 0.629167 4.68333 0 6.5 0C8.31667 0 9.85417 0.629167 11.1125 1.8875C12.3708 3.14583 13 4.68333 13 6.5C13 7.23333 12.8833 7.925 12.65 8.575C12.4167 9.225 12.1 9.8 11.7 10.3L18 16.6L16.6 18ZM6.5 11C7.75 11 8.8125 10.5625 9.6875 9.6875C10.5625 8.8125 11 7.75 11 6.5C11 5.25 10.5625 4.1875 9.6875 3.3125C8.8125 2.4375 7.75 2 6.5 2C5.25 2 4.1875 2.4375 3.3125 3.3125C2.4375 4.1875 2 5.25 2 6.5C2 7.75 2.4375 8.8125 3.3125 9.6875C4.1875 10.5625 5.25 11 6.5 11Z"
                    fill="#474747"
                  />
                </svg>
              }
            />
          </div>

          {/* Filter Button */}
          <div className="relative w-full md:w-auto" ref={filterRef}>
            <div
              className="flex items-center justify-end md:justify-start gap-1 cursor-pointer card p-3 text-text-secondary"
              onClick={() => setShowFilters((prev) => !prev)}
            >
              <svg
                width="18"
                height="12"
                viewBox="0 0 18 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 12V10H11V12H7ZM3 7V5H15V7H3ZM0 2V0H18V2H0Z"
                  fill="#474747"
                />
              </svg>
              <span>Filter</span>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="absolute top-12 right-0 z-50 shadow-xl shadow-gray-300">
                <div className="card w-full md:min-w-120 p-4">
                  {/* Status */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-base md:text-xl text-text-primary">Status</span>
                    <div className="flex gap-2">
                      {['ACTIVE', 'INACTIVE'].map((status) => (
                        <button
                          key={status}
                          onClick={() =>
                            handleStatusClick(status as 'ACTIVE' | 'INACTIVE')
                          }
                          className={`md:rounded-full rounded-lg text-xs md:text-base px-2 py-2 ${
                            filters.status === status
                              ? 'bg-black text-white'
                              : 'text-text-secondary'
                          }`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Type */}
                  <FilterRow
                    label="Type"
                    options={typeOptions}
                    value={filters.type || ''}
                    onChange={(val) => handleSelect('type', val as string)}
                  />

                  {/* Bed Utilization */}
                  <FilterRow
                    label="Bed Utilization Rate"
                    options={bedRateOptions}
                    value={filters.bedUtilization || ''}
                    onChange={(val) =>
                      handleSelect('bedUtilization', val as string)
                    }
                  />

                  {/* Program Fees */}
                  {/* <FilterRow
                    label="Program Fees Range"
                    options={bedRateOptions}
                    value={filters.programFeesOrder || ''}
                    onChange={(val) =>
                      handleSelect('programFeesOrder', val as string)
                    }
                  /> */}

                  {/* Manager */}
                  <FilterRow
                    label="Assigned House Manager"
                    options={managerOptions}
                    value={filters.assignedManager || ''}
                    onChange={(val) =>
                      handleSelect('assignedManager', val as string)
                    }
                  />

                  {/* Location */}
                  <div className="flex flex-col sm:flex-row items-start md:items-center justify-between gap-2 md:gap-4 mb-4">
                    <span className="text-sm md:text-xl text-text-primary">Location</span>
                    <Input
                      placeholder="Enter location"
                      value={filters.location || ''}
                      onChange={handleLocationChange}
                      className="!w-full !min-w-[200px]"
                    />
                  </div>

                  {/* clear filters  */}
                  <div className="flex justify-end">
                    <Button className="" onClick={clearFilterHandler}>
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ================== REUSABLE ROW COMPONENT ==================
interface FilterRowProps {
  label: string;
  options: Option[];
  value: string | number;
  onChange: (val: unknown) => void;
}

const FilterRow: React.FC<FilterRowProps> = ({
  label,
  options,
  value,
  onChange,
}) => (
  <div className="flex flex-col sm:flex-row items-center justify-between mb-4">
    <span className="w-full text-sm md:text-xl text-text-primary">{label}</span>
    <Dropdown
      options={options}
      value={value}
      placeholder={`Select ${label.toLowerCase()}`}
      onChange={onChange}
      className="!w-full md:!w-auto"
      buttonClassName="!min-w-fit !gap-4 text-text-secondary !border-[0] !p-3"
    />
  </div>
);
